import React from 'react';
import { useTranslation } from 'react-i18next';
import type { BannerSlideComponent } from '../../../types/builder';
import { ImageUploader } from '../../Common/ImageUploader';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';

interface BannerSlideSettingsProps {
  component: BannerSlideComponent;
  onUpdate: (settings: BannerSlideComponent['settings']) => void;
}

export const BannerSlideSettings: React.FC<BannerSlideSettingsProps> = ({ component, onUpdate }) => {
  const { t } = useTranslation('builder');
  const { settings } = component;

  const handleChange = (field: string, value: any) => {
    onUpdate({
      ...settings,
      [field]: value,
    });
  };

  const handleSlideChange = (slideId: string, field: string, value: string) => {
    const updatedSlides = settings.slides.map((slide) =>
      slide.id === slideId ? { ...slide, [field]: value } : slide
    );
    handleChange('slides', updatedSlides);
  };

  const addSlide = () => {
    const newSlide = {
      id: `slide-${Date.now()}`,
      image: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=1200&h=400&fit=crop',
      link: '#',
      title: 'New Slide',
    };
    handleChange('slides', [...settings.slides, newSlide]);
  };

  const removeSlide = (slideId: string) => {
    if (settings.slides.length <= 1) {
      alert(t('settings.bannerSlide.atLeastOneSlide'));
      return;
    }
    handleChange('slides', settings.slides.filter((s) => s.id !== slideId));
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            {t('settings.bannerSlide.slides')}
          </label>
          <button
            onClick={addSlide}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.bannerSlide.addSlide')}
          </button>
        </div>

        <div className="space-y-4">
          {settings.slides.map((slide, index) => (
            <div key={slide.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.bannerSlide.slideNumber', { n: index + 1 })}
                </span>
                {settings.slides.length > 1 && (
                  <button
                    onClick={() => removeSlide(slide.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.bannerSlide.title')}</label>
                  <input
                    type="text"
                    value={slide.title}
                    onChange={(e) => handleSlideChange(slide.id, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.bannerSlide.linkUrl')}</label>
                  <input
                    type="text"
                    value={slide.link}
                    onChange={(e) => handleSlideChange(slide.id, 'link', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t('settings.bannerSlide.image')}</label>
                  <ImageUploader
                    currentImage={slide.image}
                    onImageChange={(url) => handleSlideChange(slide.id, 'image', url)}
                    aspectRatio="21:9"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.bannerSlide.autoPlay')}
          checked={settings.autoPlay}
          onChange={(checked) => handleChange('autoPlay', checked)}
        />
      </div>

      {settings.autoPlay && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.bannerSlide.autoPlaySpeedMs')}
          </label>
          <input
            type="number"
            value={settings.autoPlaySpeed}
            onChange={(e) => handleChange('autoPlaySpeed', parseInt(e.target.value))}
            step="1000"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
          />
        </div>
      )}

      <div>
        <ToggleSwitch
          label={t('settings.bannerSlide.showArrows')}
          checked={settings.showArrows}
          onChange={(checked) => handleChange('showArrows', checked)}
        />
      </div>

      <div>
        <ToggleSwitch
          label={t('settings.bannerSlide.showDots')}
          checked={settings.showDots}
          onChange={(checked) => handleChange('showDots', checked)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.bannerSlide.heightPx')}
        </label>
        <input
          type="number"
          value={settings.height}
          onChange={(e) => handleChange('height', parseInt(e.target.value))}
          step="50"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('settings.common.padding')}
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={settings.padding}
          onChange={(e) => handleChange('padding', parseInt(e.target.value))}
          className="w-full"
        />
        <div className="text-sm text-gray-600 text-center mt-1">{settings.padding}px</div>
      </div>
    </div>
  );
};

